import { FormikTouched } from 'formik';
import { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as vp from 'vopay';
import moment from 'moment';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { PendingDataProvider, usePendingData } from '@/providers/PendingData.provider';
import FundAccountForm from './FundAccountForm';
import { FundRequest, FundAccountHandler } from './types';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import usePostEftFund from '@/hooks/useApi/usePostEftFund';
import usePostScheduledEftFund from '@/hooks/useApi/usePostScheduledEftFund';
import usePostFundMyAccountSchedule from '@/hooks/useApi/usePostFundMyAccountSchedule';
import usePostACHFundSchedule from '@/hooks/useApi/usePostACHFundSchedule';
import usePostACHFund from '@/hooks/useApi/usePostACHFund';
import usePostFundMyAccount from '@/hooks/useApi/usePostFundMyAccount';
import { useOpenModule } from '@/providers/OpenModule.provider';
import FundAccountDialogueContext, { FundAccountCtx } from './Dialogue/FundAccountContext';
import { DialogueHandlerProps, FundAccountDialogueHandler } from './Dialogue/types';
import FundAccountDialogue from './Dialogue/FundAccountDialogue';
import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

type TokenProps = {
  token: string;
  cardToken: string;
  type: string;
  currency: string;
  interacID: string;
  account?: {
    RecipientPhoneNumber?: string;
    RecipientName?: string;
    RecipientEmailAddress?: string;
    Question?: string;
    Answer?: string;
    ClientReferenceNumber?: string;
  };
};

const { Box, Title } = vp;

type FC = FunctionComponent;

const FundAccountContainer: FC = () => {
  const reviewRef = useRef<HTMLDivElement>(null);
  const { state } = useLocation<TokenProps>();
  const { userSettings } = useUserSettingsProvider();
  const currencyProp = state?.currency;
  const typeProp = state?.type;
  const history = useHistory();
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});

  const {
    response: { ClientAccountsEnabled, Country, AccountID },
  } = useClientData();
  const [defaultBankAccountResponse, getDefaultBankAccount] = useFetchDefaultBankAccount({});
  const [, , , fetchBankAccounts] = useFetchClientBankAccounts({});
  const selectedCurrency = currencyProp !== undefined ? currencyProp : Country === 'US' ? 'USD' : 'CAD';
  const selectedPaymentMethod = typeProp !== undefined ? typeProp : 'Bank Account';
  const [clientAccount, setClientAccount] = useState<vp.ClientAccount | undefined>();

  const [formData, setFormData] = useState<FundRequest>({
    Currency: selectedCurrency,
    PaymentMethod:
      selectedPaymentMethod === 'Interac e-Transfer' || selectedPaymentMethod === 'Interac'
        ? 'Interac e-Transfer Money Request'
        : selectedPaymentMethod,
    Account: '',
    Amount: 0,
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'Monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    MessageForRecipient: '',
    Notes: `Fund via 'Fund My Account'`,
    ClientAccount: '',
    EndingAfterPayments: '',
    TransactionLabel: '',
    IdentificationNumber: '',
  });
  const clientAccountsEnabled = ClientAccountsEnabled === '1';
  const [selectedBank, setSelectedBank] = useState<vp.ClientBankAccountsLists | undefined>();

  useEffect(() => {
    if (clientAccountsEnabled) return;

    const operationalBank = Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount || {}).find(
      (b) => b.Currency === formData.Currency,
    );

    if (!operationalBank) return;

    fetchBankAccounts({
      Token: operationalBank.Token,
      ClientControlled: true,
      Timezone: userSettings.timezone,
    }).then((res) => {
      if (!res.Success) throw new Error(res.ErrorMessage);

      const bank = res.BankAccounts[0];

      setSelectedBank(bank);

      setFormData((prev) => ({
        ...prev,
        Account: `${bank.Currency} ${bank.AccountHolderName} ${vp.getMaskedNumber(bank.AccountNumber)}`,
      }));
    });
  }, [defaultBankAccountResponse, formData.Currency]);

  useEffect(() => {
    if (!clientAccountsEnabled) return;

    init();

    async function init() {
      const clientAccountsRes = await fetchClientAccounts({
        ClientType: '',
      });

      if (!clientAccountsRes.Success) throw new Error(clientAccountsRes.ErrorMessage);

      const primaryClientAccount = Object.values(clientAccountsRes.ClientAccounts || {}).find(
        (obj) => obj.IsPrimaryAccount === '1',
      );

      if (!primaryClientAccount) return;

      setClientAccount(primaryClientAccount);

      setFormData((prev) => ({
        ...prev,
        ClientAccount: primaryClientAccount.ClientAccountID,
      }));

      const bankAccountsRes = await fetchBankAccounts({
        ClientAccountID: primaryClientAccount.ClientAccountID,
        Timezone: userSettings.timezone,
      });

      if (!bankAccountsRes.Success) throw new Error(bankAccountsRes.ErrorMessage);

      const defaultBankAccount = Object.values(bankAccountsRes.BankAccounts || {}).find((obj) => obj.IsDefault === '1');

      if (!defaultBankAccount) {
        showValidationError(
          `There is no default bank account set up for client account ID: ${primaryClientAccount.ClientAccountID}`,
        );
      }

      setSelectedBank(defaultBankAccount);
    }
  }, []);

  const [formTouched, setFormTouched] = useState<FormikTouched<FundRequest>>({});
  const { getModuleTitle } = useNavMenu();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [showSubmitButton] = useState(true);
  const [ValuesPresent, setValuesPresent] = useState(false);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);

  useEffect(() => {
    if (clientAccountsEnabled) return;

    getDefaultBankAccount({
      Timezone: userSettings.timezone,
    });
  }, []);

  const { setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const { reset } = usePendingData();
  const [eftFundResponse] = usePostEftFund({});
  const [scheduledEftFundResponse] = usePostScheduledEftFund({});
  const [fundMyAccountScheduleResponse, postFundMyAccountSchedule] = usePostFundMyAccountSchedule({});
  const [fundMyAccountResponse, postFundMyAccount] = usePostFundMyAccount({});
  const [shouldFundMyAccount, setShouldFundMyAccount] = useState(false);
  const [shouldFundMyAccountSchedule, setShouldFundMYAccountSchedule] = useState(false);
  const [shouldFundEft, setShouldFundEft] = useState(false);
  const [shouldFundScheduledEft, setShouldFundScheduledEft] = useState(false);
  const [ACHFundScheduleResponse] = usePostACHFundSchedule({});
  const [ACHFundTransactionResponse] = usePostACHFund({});
  const [shouldACHFund, setShouldACHFund] = useState(false);
  const [shouldACHFundSchedule, setShouldACHFundSchedule] = useState(false);
  const [showFundAccountErrorAlert, setShowFundAccountErrorAlert] = useState(false);
  const [fundAccountError, setFundAccountError] = useState('');

  const [common, setCommon] = useState({
    PortalUser: '',
    Amount: 0,
    CompanyName: '',
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    AccountNumber: '',
    ABARoutingNumber: '',
    FirstName: '',
    LastName: '',
    PhoneNumber: '',
    Address1: '',
    City: '',
    Province: '',
    Country: '',
    PostalCode: '',
    Token: '',
    TransactionLabel: '',
  });
  const [, setBankDetail] = useState({
    PostalCode: '',
    ClientReferenceNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    PlaidAccountID: '',
    AccountHolderName: '',
    BankId: '',
    InstitutionName: '',
    Currency: '',
  });

  const FundAccount: FundAccountCtx = {
    showFundAccountErrorAlert,
    fundAccountError,
  };

  useEffect(() => {
    const bankAccountDetail = selectedBank;

    if (bankAccountDetail) {
      const nameParts = bankAccountDetail?.AccountHolderName?.trim().split(' ') || '';
      const isCompanyName =
        (nameParts.length > 2 || nameParts.length < 2) && formData.PaymentMethod !== 'VoPay Instant';

      setCommon({
        PortalUser: AccountID || '',
        Amount: formData.Amount || 0,
        FinancialInstitutionNumber: clientAccountsEnabled ? '' : bankAccountDetail.FinancialInstitutionNumber || '',
        BranchTransitNumber: clientAccountsEnabled ? '' : bankAccountDetail.BranchTransitNumber || '',
        AccountNumber: clientAccountsEnabled ? '' : bankAccountDetail.AccountNumber || '',
        ABARoutingNumber: clientAccountsEnabled ? '' : bankAccountDetail.ABARoutingNumber || '',
        FirstName: !isCompanyName ? bankAccountDetail.AccountHolderName.split(' ')[0] : '',
        LastName: !isCompanyName ? bankAccountDetail.AccountHolderName.split(' ')[1] : '',
        CompanyName: isCompanyName ? bankAccountDetail.AccountHolderName : '',
        PhoneNumber: vp.NumbersOnly(bankAccountDetail.Phone || ''),
        Address1: bankAccountDetail.Address || '',
        City: bankAccountDetail.City || '',
        Province: bankAccountDetail['Province/State'] || '',
        PostalCode: bankAccountDetail['PostalCode/ZipCode'] || '',
        Country: Country || '',
        Token: clientAccountsEnabled ? '' : bankAccountDetail?.Token || '',
        TransactionLabel: formData.TransactionLabel || '',
      });

      setBankDetail({
        PostalCode: bankAccountDetail['PostalCode/ZipCode'],
        ClientReferenceNumber: bankAccountDetail.ClientReferenceNumber,
        FlinksAccountID: clientAccountsEnabled ? '' : bankAccountDetail.FlinksAccountID,
        FlinksLoginID: clientAccountsEnabled ? '' : bankAccountDetail.FlinksLoginID,
        PlaidAccountID: clientAccountsEnabled ? '' : bankAccountDetail.PlaidAccountID,
        AccountHolderName: bankAccountDetail.AccountHolderName,
        BankId: bankAccountDetail.BankID,
        InstitutionName: bankAccountDetail.InstitutionName,
        Currency: bankAccountDetail.Currency,
      });
    }
  }, [selectedBank, formData]);

  const handlersForm = {
    handleFundAccountDialogue: (handlerProps: DialogueHandlerProps<FundAccountDialogueHandler>) => {
      const { type, state: stateInside, action = 'default' } = handlerProps;
      const dialogueHandlers: FundAccountDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowFundAccountErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][stateInside];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onFundAccount: (_values: FundRequest) => {
      if (
        formData.PaymentMethod === 'Bank Account' &&
        formData.ScheduleType === 'single' &&
        formData.TakeFromAccountOn &&
        vp.IsTodayDate(moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD'))
      ) {
        setShouldFundMyAccount(true);
        postFundMyAccount({
          ...common,
          Currency: formData.Currency || '',
          Notes: formData.Notes || '',
          ClientAccountID: clientAccountsEnabled ? formData.ClientAccount : '',
        });
        reset();
      } else if (
        (formData.PaymentMethod === 'Bank Account' && formData.ScheduleType === 'recurring') ||
        (formData.TakeFromAccountOn && !vp.IsTodayDate(moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD')))
      ) {
        setShouldFundMYAccountSchedule(true);

        postFundMyAccountSchedule({
          Amount: common.Amount,
          Frequency: formData.ScheduleType || '',
          NameOfFrequency: formData.Frequency || '',
          SemiMonthlyFrequencyType: formData.SemiMonthlyDates || '',
          ScheduleStartDate:
            formData.ScheduleType === 'recurring'
              ? moment(Number(formData.StartDate)).format('YYYY-MM-DD') || ''
              : moment(Number(formData.TakeFromAccountOn)).format('YYYY-MM-DD') || '',
          ScheduleEndDate:
            formData.Ending === 'End Date' && formData.ScheduleType === 'recurring'
              ? moment(Number(formData.EndDate)).format('YYYY-MM-DD') || ''
              : '',
          Currency: formData.Currency || '',
          EndingAfterPayments: formData.Ending === 'Payments' ? formData.EndingAfterPayments || '' : '',
          Description: formData.Description || '',
        });

        reset();
      }
    },
  };

  const handlers: FundAccountHandler = {
    onFundAccount: (_values) => {
      if (isUnderReview) {
        setFormData(_values);
        handlersForm.onFundAccount(_values);
      } else {
        setFormData({
          ..._values,
          Amount: Number(String(_values.Amount)?.replaceAll(',', '')),
        });
        setIsUnderReview(true);
      }
    },
    onFundChange: (_values, _errors, touched) => {
      setFormTouched(touched);
      setFormData(_values);
    },
  };

  useEffect(() => {
    if (shouldFundEft) {
      const { Success, ErrorMessage } = eftFundResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTFunding/${eftFundResponse.TransactionID}/${
            formData.Currency || ''
          }/`,
          state: {
            transactionId: eftFundResponse.TransactionID,
            activeModule: 'fundMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }
      setShouldFundEft(false);
    }
    if (shouldFundMyAccount && fundMyAccountResponse) {
      const { Success, ErrorMessage } = fundMyAccountResponse;

      if (Success === true) {
        if (Country === 'US') {
          history.push({
            pathname: `/transactions-history/details/ACHFunding/${fundMyAccountResponse.TransactionID}/${
              formData.Currency || ''
            }/`,
            state: {
              transactionId: fundMyAccountResponse.TransactionID,
              activeModule: 'fundMyAccount',
              hasNewTransactionButton: true,
            },
          });
        } else {
          history.push({
            pathname: `/transactions-history/details/EFTFunding/${fundMyAccountResponse.TransactionID}/${
              formData.Currency || ''
            }/`,
            state: {
              transactionId: fundMyAccountResponse.TransactionID,
              activeModule: 'fundMyAccount',
              hasNewTransactionButton: true,
            },
          });
        }

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }
      setShouldFundEft(false);
    }

    if (shouldFundScheduledEft === true) {
      const { Success, ErrorMessage } = scheduledEftFundResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${scheduledEftFundResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: scheduledEftFundResponse.ScheduledTransactionID,
            activeModule: 'fundMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }
      setShouldFundScheduledEft(false);
    }
    if (shouldFundMyAccountSchedule === true && fundMyAccountScheduleResponse) {
      const { Success, ErrorMessage } = fundMyAccountScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${fundMyAccountScheduleResponse?.ScheduledTransactionID}/`,
          state: {
            transactionId: fundMyAccountScheduleResponse.ScheduledTransactionID,
            activeModule: 'fundMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }
      setShouldFundMYAccountSchedule(false);
    }
    if (shouldACHFundSchedule === true && ACHFundScheduleResponse) {
      const { Success, ErrorMessage } = ACHFundScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/scheduled-transactions/details/${ACHFundScheduleResponse.ScheduledTransactionID}/`,
          state: {
            transactionId: ACHFundScheduleResponse.ScheduledTransactionID,
            activeModule: 'fundMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('transactions');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }
      setShouldACHFundSchedule(false);
    }
    if (ACHFundTransactionResponse && shouldACHFund) {
      const { Success, ErrorMessage } = ACHFundTransactionResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/EFTFunding/${ACHFundTransactionResponse.TransactionID}/${
            formData.Currency || ''
          }/`,
          state: {
            transactionId: ACHFundTransactionResponse.TransactionID,
            activeModule: 'withdrawMyAccount',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowFundAccountErrorAlert(true);
        setFundAccountError(ErrorMessage);
      }

      setShouldACHFund(false);
    }
  }, [
    eftFundResponse,
    scheduledEftFundResponse,
    ACHFundScheduleResponse,
    fundMyAccountResponse,
    fundMyAccountScheduleResponse,
    ACHFundTransactionResponse,
  ]);

  useEffect(() => {
    if (submissionConfirmed && ValuesPresent) {
      setSubmissionConfirmed(false);
      setValuesPresent(false);
    }
  }, [submissionConfirmed, ValuesPresent]);

  useEffect(() => {
    if (isUnderReview) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isUnderReview]);

  return (
    <div ref={reviewRef}>
      <Box mb="md" jc="sb" flx="col" gap="lg">
        <Box mb="md" jc="sb">
          {isUnderReview ? <Title title="Review and Submit" /> : <Title title={getModuleTitle()} />}
        </Box>
        <PendingDataProvider>
          <FundAccountDialogueContext.Provider value={FundAccount}>
            <FundAccountForm
              handlers={handlers}
              clientAccount={clientAccount}
              defaultValues={formData}
              defaultTouches={formTouched}
              isUnderReview={isUnderReview}
              setIsUnderReview={setIsUnderReview}
              showSubmitButton={showSubmitButton}
              hasBankAccount={!!selectedBank}
            />
            <FundAccountDialogue handleDialogue={handlersForm.handleFundAccountDialogue} />
          </FundAccountDialogueContext.Provider>
        </PendingDataProvider>
      </Box>
    </div>
  );
  function showValidationError(ErrorMessage: string) {
    setShowFundAccountErrorAlert(true);
    setFundAccountError(ErrorMessage);
  }
};

export default FundAccountContainer;
